import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { fetchAuthSession } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';

// Component Imports
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Page Imports
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import MembershipPage from './pages/MembershipPage';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import ConfirmPage from './pages/ConfirmPage';
import SuccessPage from './pages/SuccessPage';
import OAuthRedirectHandler from './pages/OAuthRedirectHandler';
import ProfilePage from './pages/ProfilePage';
import AdminDashboard from './pages/AdminDashboard';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import WaiverPage from './pages/WaiverPage';
import NewsAndGuidesPage from './pages/NewsAndGuidesPage';
import InnovatorSimple from './pages/InnovatorSimple';
import NewsPage from './pages/NewsPage';
import BlogPage from './pages/BlogPage';
import FollowUsPage from './pages/FollowUsPage'; // near other imports
import FounderMohit from './pages/FounderMohit';
import DirectorKaitlyn from './pages/DirectorKaitlyn';




// Robo Kids Pages
import RoboKidsLanding from './pages/RoboKidsLanding';
import RoboKidsPage from './pages/RoboKidsPage1';
import RoboKidsPage1 from './pages/RoboKidsPage1'; // Basic
import RoboKidsPage2 from './pages/RoboKidsPage2'; // Intermediate
import RoboKidsPage3 from './pages/RoboKidsPage3'; // Advanced
import RoboKidsPage4 from './pages/RoboKidsPage4'; // Custom
import RoboKidsPaymentPage from './pages/RoboKidsPaymentPage';
import RoboKidsSuccessPage from './pages/RoboKidsSuccessPage';
import RoboKidsReceiptPage from './pages/RoboKidsReceiptPage';



// Guide Pages
import LightSwitchGuide from './pages/guides/LightSwitchGuide';
import MotorGuide from './pages/guides/MotorGuide';
import ServoGuide from './pages/guides/ServoGuide';
import Welcome from './pages/news/Welcome';
import SecurityBasics from './pages/news/SecurityBasics';
import FallLaunch from './pages/news/FallLaunch';
import BugBots from './pages/news/BugBots';
import RoboticsDashboard from './pages/RoboticsDashboard';


const userIsAdmin = false; // Placeholder until you connect to auth

function CookieBanner() {
  const [accepted, setAccepted] = useState(localStorage.getItem('cookiesAccepted') === 'true');

  useEffect(() => {
    if (accepted) {
      localStorage.setItem('cookiesAccepted', 'true');
    }
  }, [accepted]);

  if (accepted) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      width: '100%',
      backgroundColor: '#222',
      color: 'white',
      textAlign: 'center',
      padding: '0.75rem',
      zIndex: 999
    }}>
      <span>
        We use cookies to improve your experience.{' '}
        <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#0bf' }}>Privacy Policy</a>
        {' | '}
        <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#0bf' }}>Terms</a>
      </span>
      <button onClick={() => setAccepted(true)} style={{
        marginLeft: '1rem',
        padding: '0.5rem 1rem',
        background: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}>
        Accept
      </button>
    </div>
  );
}

function App() {
  const [userEmail, setUserEmail] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const session = await fetchAuthSession();
        const email = session.tokens?.idToken?.payload?.email;
        setUserEmail(email || null);
      } catch {
        setUserEmail(null);
      }
    };

    fetchUser();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      if (event === 'signIn' || event === 'signOut') {
        fetchUser();
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <>
      <Navbar userEmail={userEmail} />
      <main style={{ flex: '1 0 auto', paddingTop: '60px' }}>
        <Routes>
          {/* Main Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/news-guides" element={<NewsAndGuidesPage />} />
          <Route path="/innovator" element={<InnovatorSimple />} />
          <Route path="/robots" element={<RoboticsDashboard />} />

          {/* Authentication Routes */}
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/membership/register" element={<RegisterPage />} />
          <Route path="/membership/login" element={<LoginPage />} />
          <Route path="/confirm" element={<ConfirmPage />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/oauth-redirect" element={<OAuthRedirectHandler />} />
          <Route path="/profile" element={<ProfilePage />} />

          {/* Guide Routes */}
          <Route path="/guides/light-switch" element={<LightSwitchGuide />} />
          <Route path="/guides/motor-guide" element={<MotorGuide />} />
          <Route path="/guides/servo-guide" element={<ServoGuide />} />
          <Route path="/news/welcome" element={<Welcome />} />
          <Route path="/news/security-basics" element={<SecurityBasics />} />
          <Route path="/news/fall-launch" element={<FallLaunch />} />

          {/* Robo Kids Routes */}
          <Route path="/robokidslanding" element={<RoboKidsLanding />} />
          <Route path="/robokids" element={<RoboKidsPage />} />
          <Route path="/robokidspage1" element={<RoboKidsPage1 />} />
          <Route path="/robokidspage2" element={<RoboKidsPage2 />} />
          <Route path="/robokidspage3" element={<RoboKidsPage3 />} />
          <Route path="/robokidspage4" element={<RoboKidsPage4 />} />
          <Route path="/robokids-payment" element={<RoboKidsPaymentPage />} />
          <Route path="/robokids/success" element={<RoboKidsSuccessPage />} />
          <Route path="/robokids-success" element={<RoboKidsSuccessPage />} /> {/* alias for redirect safety */}
          <Route path="/robokids/receipt" element={<RoboKidsReceiptPage />} />
          <Route path="/robokids/waiver" element={<WaiverPage />} />
          <Route path="/waiver" element={<WaiverPage />} />
          {/* Redirect old guides path to the new page */}
          <Route path="/guides" element={<Navigate to="/news-guides" replace />} />
          <Route path="/follow" element={<FollowUsPage />} />
          <Route path="/news/bugbots" element={<BugBots />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/about/founder-mohit-singh" element={<FounderMohit />} />
          <Route path="/about/director-kaitlyn-lowery" element={<DirectorKaitlyn />} />


          {/* Admin Route */}
          <Route
            path="/admin"
            element={userIsAdmin ? <AdminDashboard /> : <Navigate to="/" replace />}
          />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default App;
